<template lang="pug">
window(title="Profile", :resizable="true", @resize="$refs.chart.resize && $refs.chart.resize()", :width="640", :height="480", :closeButton="true", @closebuttonclick="remove", :zGroup="1")
  .contents
    .header
      template(v-if="Env.releases.cutout.length > 1")
        | Release:&nbsp;
        select(v-model="profile.release")
          option(v-for="o in Env.releases.cutout", :key="Id(o)", :value="o") {{o.name}}
      | Rerun:
      select(v-model="profile.rerun")
        option(v-for="o in profile.release.reruns") {{o}}
      | Filter:
      select(v-model="profile.filter")
        option(v-for="f in Env.filters") {{f.value}}
      span(v-html="lineLength", style="margin-left: 1em;")
      .padding
      button-menu
        template(v-if="Env.databaseService")
          menu-item(:label=`$t('load_profile_from_fits')`, @click="profile.downloadAsFits", :disbaled="true")
    .chart-wrapper
      Plotly.chart(
        :plotlyData="plotlyData", :plotlyLayout="plotlyLayout", :plotlyConfig="plotlyConfig", ref="chart",
        @plotly_hover="plotlyHover", @plotly_unhover="plotlyUnhover", @plotly_click="plotlyClick")
</template>


<script lang="ts">
import { Vue, Component, Prop } from "vue-property-decorator";
import { ObjectProfileModule } from "@/modules/ssp_database/ObjectProfileModule";
import { Profile } from "@/modules/ssp_database/ObjectProfileModule/Profile";
import { MarkerLayer } from "@/modules/CatalogOverplotModule/Marker";
import { MarkerStyle } from "@/modules/CatalogOverplotModule/Marker/marker";
import { V3 } from "@/utils/math";
import { EquatorialCoord } from "@hscmap/angle";
import Loading from '@/components/Loading.vue'
import { vec3 } from 'gl-matrix';
import { angle60html } from '@/utils/angle';
import { store } from '@/store';

@Component({
  components: {
    Plotly: () => ({
      component: import('@/components/Plotly'),
      loading: Loading,
      delay: 0,
    }) as any,
  },
})
export default class extends Vue {
  @Prop({ required: true })
  module!: ObjectProfileModule

  @Prop({ required: true })
  profile!: Profile

  plotlyHover(e: Plotly.PlotMouseEvent) {
    const i = e.points[0].pointNumber
    this.focus(i)
  }

  plotlyUnhover(e: Plotly.PlotMouseEvent) {
    this.clearFocus()
  }

  plotlyClick(e: Plotly.PlotMouseEvent) {
    const i = e.points[0].pointNumber
    const globe = store.currentGlobe()
    const coord = EquatorialCoord.fromXyz(this.i2xyz(i))
    globe.viewFactory.jumpTo({}, { coord })
  }

  get lineLength() {
    const { c0, c1 } = this.profile.line!
    const d = vec3.dist(c0.xyz, c1.xyz)
    const t = 2 * Math.asin(d / 2)
    return `${angle60html(t)}`
  }

  private focusLayer?: MarkerLayer

  private focus(i: number) {
    this.clearFocus()
    const globe = store.currentGlobe()
    this.focusLayer = new MarkerLayer([this.i2xyz(i)], [1, 1, 1, 1], MarkerStyle.CROSS3)
    globe.addLayer(this.focusLayer)
  }

  private i2xyz(i: number): V3 {
    const n = 512
    const t = i / (n - 1)
    const xyz0 = this.profile.line.c0.xyz
    const xyz1 = this.profile.line.c1.xyz
    return [
      t * xyz1[0] + (1 - t) * xyz0[0],
      t * xyz1[1] + (1 - t) * xyz0[1],
      t * xyz1[2] + (1 - t) * xyz0[2],
    ]
  }

  private clearFocus() {
    const globe = store.currentGlobe()
    this.focusLayer && globe.removeLayer(this.focusLayer)
    this.focusLayer = undefined
  }

  remove() {
    const i = this.module.profiles.indexOf(this.profile)
    this.module.profiles.splice(i, 1)
  }

  get plotlyData(): Array<Partial<Plotly.ScatterData>> {
    return [{
      x: this.profile.x,
      y: this.profile.y,
      // type: 'scattergl',
      type: 'scatter',
    }]
  }

  get plotlyLayout(): Partial<Plotly.Layout> {
    return {
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      margin: {
        l: 80,
        t: 20,
        b: 20,
        r: 0,
      },
      autosize: true,
      font: {
        color: 'white',
      },
      xaxis: { gridcolor: 'rgba(255, 255, 255, 0.5)' },
      yaxis: { gridcolor: 'rgba(255, 255, 255, 0.5)' },
    }
  }

  get plotlyConfig(): Partial<Plotly.Config> {
    return {
      displayModeBar: true,
    }
  }
}
</script>


<style lang="scss" scoped>
.contents {
  display: flex;
  flex-flow: column;
  height: 100%;
}

.header {
  display: flex;
}

.padding {
  flex-grow: 1;
}

.chart-wrapper {
  flex-grow: 1;
  position: relative;
}

.chart {
  position: absolute;
  width: 100%;
  height: 100%;
}
</style>

<i18n>
en:
  load_profile_from_fits: Load Profile from Fits
ja:
  load_profile_from_fits: プロファイルをFITSから読み込み

</i18n>